import { sendGAEvent } from '@next/third-parties/google';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import Vinyl from '@/app/assets/imgs/vinyl.png';
import { Fail } from '@/app/components/icons/Fail';
import { Loading } from '@/app/components/icons/Loading';
import type { ActionDialogProps } from '@/app/components/shared/ActionDialog';
import { Button } from '@/app/components/ui/button';
import { AUDIO_DURATION, SITE } from '@/app/constants';
import { useDevice, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { type CreationInfo, type CreationWithTask, EllipsisActionTypeEnum, type TaskInfo } from '@/app/types';
import { cn, getReportParams, reportEvent } from '@/app/utils';
import { convertTTSPromptsToTextPrompt, getBlurBackgroundImg } from '@/app/utils/client';
import { FINISHED_STATES } from '../constants';
import type { DisplayTaskState, TaskStateInfo } from '../types';
import { getEstimateTime, getQueueI18nMessageConfig } from '../utils';
import { getAudioButtonConfig } from '../utils/button-config';
import { AudioPlayer, type AudioPlayerRef } from './AudioPlayer';
import { CancelButton } from './CancelButton';
import { CreationActionButtons } from './CreationActionButtons';
import { CustomProgress } from './CustomProgress';

interface AudioCreationCardProps {
  creation?: CreationInfo;
  task: TaskInfo;
  taskState: DisplayTaskState;
  queueWaitTime?: TaskStateInfo['queue_wait_time'];
  isWaitTimeProlonged: boolean;
  onOpenActionDialog: (dialogAction: ActionDialogProps) => void;
  onOpenDetail: (creation: CreationWithTask) => void;
  onCancelTask?: (id: string) => void;

  // CreationActionButtons 相关 props
  onSubmit?: (creation: CreationInfo) => void;
}

export const AudioCreationCard: React.FC<AudioCreationCardProps> = ({
  creation,
  task,
  taskState,
  queueWaitTime,
  isWaitTimeProlonged,
  onOpenActionDialog,
  onOpenDetail,
  onCancelTask,
  onSubmit,
}) => {
  const t = useTranslations();
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { open: openDeleteDialog } = useGlobalModal('deleteDialog');
  const { isMobile } = useDevice();

  const { userInfo, removeTask } = useStore((state) => ({
    userInfo: state.userInfo,
    removeTask: state.removeTask,
  }));
  const [playId, setPlayId] = useState<string | null>(null);
  const audioRef = useRef<AudioPlayerRef>(null);

  const {
    settings,
    type,
    id: taskId,
    scene,
    input: { prompts },
  } = task;
  const { id = '', uri = '', audio_image, duration = AUDIO_DURATION } = creation || {};

  const reportParams = { ...getReportParams(type, settings, scene), interaction_position: `${type}_task_card` };
  const hasFinished = FINISHED_STATES.includes(taskState);
  const isTtsTask = (task.type as string) === 'tts';
  const prompt = isTtsTask
    ? convertTTSPromptsToTextPrompt(prompts)
    : prompts?.filter((item) => item.type === 'text')[0]?.content;

  const handleSelectedAction = (action: EllipsisActionTypeEnum) => {
    // 删除按钮是任务维度
    if (action === EllipsisActionTypeEnum.Delete) {
      openDeleteDialog({
        type: 'task',
        creationId: '',
        taskId,
        reportParams,
        creationType: creation?.type,
        onDelete: () => {
          removeTask(taskId);
        },
      });
      return;
    }

    // 如果有 creationId，则上报 creation 维度，否则上报 task 维度
    if (action === EllipsisActionTypeEnum.Report) {
      onOpenActionDialog({
        action: EllipsisActionTypeEnum.Report,
        type: id ? 'creation' : 'task',
        id: id || taskId,
        reportParams,
      });
      return;
    }

    // 其他按钮是生成物维度
    onOpenActionDialog({
      action,
      type: 'creation',
      id: id || taskId,
      reportParams,
    });
  };

  const showActionList = getAudioButtonConfig({ creation, task });

  const renderStateCard = ({
    icon,
    title,
    content,
  }: {
    title?: React.ReactNode;
    icon?: React.ReactNode;
    content: React.ReactNode | string;
  }) => {
    return (
      <div
        className={cn(
          'flex max-w-[600px] justify-between gap-8 rounded-12 bg-cover bg-no-repeat py-3 pr-4 pl-3',
          taskState === 'success' ? 'cursor-pointer bg-transparent hover:bg-white/10' : 'cursor-default bg-black/[48%]',
          id === playId && 'gap-10 md:gap-8',
        )}
        style={{
          backgroundImage: taskState === 'success' ? `url(${getBlurBackgroundImg(audio_image?.blurHash, 0.1)})` : '',
        }}
        onClick={() => {
          if (taskState === 'success' && creation?.id) {
            onOpenDetail({
              ...creation,
              task,
            });
          }
        }}
      >
        {icon && (
          <div className={cn('relative md:size-16', isTtsTask ? 'size-[80px]' : 'size-[104px]')}>
            <span
              className={cn(
                'absolute inset-0 z-[1] flex aspect-square flex-shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-6 text-[32px] text-system-white/40',
                taskState !== 'success' && 'cursor-auto bg-system-bg03',
              )}
              onClick={(e) => {
                e.stopPropagation();
                audioRef.current?.handlePlayPause();
              }}
            >
              {icon}
            </span>
            <img
              src={Vinyl.src}
              alt=""
              className={cn(
                'absolute inset-0 z-0 animate-spin-vinyl opacity-0 transition-[left]',
                id === playId && 'left-[30px] opacity-1 md:left-4',
              )}
            />
          </div>
        )}
        <div className="flex flex-1 flex-col justify-center overflow-hidden py-1 md:p-0">
          <h3
            className={cn('relative mb-1 font-medium text-base text-system-white leading-5', {
              hidden: taskState === 'success',
            })}
          >
            {title}
          </h3>
          <div
            className={cn('font-normal text-sm text-system-white/40', {
              'flex-1': taskState === 'success',
            })}
          >
            {content}
          </div>
        </div>
      </div>
    );
  };

  // 排队中或生成中
  const renderProgressState = () => {
    const { key, params } = getQueueI18nMessageConfig(
      queueWaitTime?.min[0] || 0,
      queueWaitTime?.max[0] || 0,
      isWaitTimeProlonged,
    );

    switch (taskState) {
      case 'queueing':
        return renderStateCard({
          icon: <Loading className={'animate-spin text-system-white'} />,
          title: t('task.status.queue.title'),
          content: (
            <div className="relative">
              <div>
                {queueWaitTime?.max[0] ? (
                  <p className="mb-1">
                    {t(
                      `task.status.queue.${key}`,
                      params
                        ? {
                            ...params,
                            unit: t(`task.status.queue.${params.unit}`),
                          }
                        : {},
                    )}
                  </p>
                ) : null}
                {userInfo?.subs_plan === 'free'
                  ? t.rich('task.status.queue.free', {
                      button: (chunks) => (
                        <Button
                          variant="text"
                          size="original"
                          className="font-normal text-sm text-system-blue02 lg:text-xs"
                          onClick={() => {
                            openPlanDetailDialog({ fromPage: 'audio_upgrade' });
                            reportEvent('vip_price_modal_exposure', { from_page: 'audio_upgrade' });
                            sendGAEvent('event', 'subscription_exposure', { from_page: 'audio_upgrade' });
                          }}
                        >
                          {chunks}
                        </Button>
                      ),
                    })
                  : t('task.status.queue.vip')}
              </div>
              <CancelButton
                className="-translate-y-1/2 absolute top-1/2 right-0 border border-system-white24 text-system-white enabled:hover:bg-system-hover02 disabled:text-system-white64 sm:hidden"
                taskId={taskId}
                onCancel={onCancelTask}
              />
            </div>
          ),
        });
      case 'processing':
      case 'fetchingTaskDetail':
        return renderStateCard({
          icon: <Loading className={'animate-spin text-system-white'} />,
          title: <div className="mb-4">{t('task.status.creating.title')}</div>,
          content: (
            <CustomProgress
              hasFinished={taskState === 'fetchingTaskDetail'}
              id={task.id}
              allWaitTime={getEstimateTime(type, settings, task.scene)}
              stopProgress={95}
            />
          ),
        });
      default:
        return null;
    }
  };

  const renderCreationResult = () => {
    // 任务已取消
    if (taskState === 'canceled') {
      return renderStateCard({
        icon: <Fail />,
        title: t('task.status.canceled.title'),
        content: t('task.status.canceled.content'),
      });
    }

    // 任务失败
    if (taskState === 'failed') {
      const taskErrorCode = task.err_code;
      if (taskErrorCode === 'CreationPolicyViolation' || taskErrorCode === 'failed') {
        return renderStateCard({
          icon: <Fail />,
          title: t('task.status.auditVideoFailed.title'),
          content: t('task.status.auditVideoFailed.content'),
        });
      }

      return renderStateCard({
        icon: <Fail />,
        title: t('task.status.otherFailed.title'),
        content: t('task.status.otherFailed.content'),
      });
    }

    // task 为 success，但是生成物失败
    const errorCode = creation?.err_info?.err_code;
    if (errorCode) {
      // 机审审核不过
      if (errorCode === 'CreationPolicyViolation' || errorCode === 'failed') {
        return renderStateCard({
          icon: <Fail />,
          title: t('task.status.auditVideoFailed.title'),
          content: t('task.status.auditVideoFailed.content'),
        });
      }

      return renderStateCard({
        icon: <Fail />,
        title: t('task.status.otherFailed.title'),
        content: t('task.status.otherFailed.content'),
      });
    }

    return renderStateCard({
      icon: <img src={audio_image?.cover[SITE]} alt="" />,
      content: (
        <div className="flex h-full flex-col justify-between">
          <div>
            <div className="flex items-center justify-between gap-3 text-system-white">
              <div id="prompt" className="flex-1 truncate font-medium text-base leading-5 md:text-sm">
                {prompt}
              </div>
              {!isMobile && (
                <div className="flex items-center md:hidden" onClick={(event) => event.stopPropagation()}>
                  <CreationActionButtons
                    creation={creation}
                    task={task}
                    showActionList={showActionList}
                    onSubmit={onSubmit}
                    onSelectedAction={handleSelectedAction}
                  />
                </div>
              )}
            </div>
            <p className="mt-1 font-normal text-sm text-system-white/40 md:mt-0">
              {duration}s
              {SITE === 'china' ? (
                <>
                  <span className="mx-1 text-system-white/20">|</span>Vidu AI 生成
                </>
              ) : (
                ''
              )}
            </p>
          </div>
          <AudioPlayer url={uri} id={id} duration={duration} onPlay={(id) => setPlayId(id)} ref={audioRef} />
        </div>
      ),
    });
  };

  return hasFinished ? renderCreationResult() : renderProgressState();
};
